import { useState, useEffect, useMemo } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";
import { AuthProvider, useAuth } from "contexts/AuthContext";
import { getRoutes, getSidenavRoutes, loadDepartmentRoutes, loadMinistryRoutes } from "routes";

// --- ProtectedRoute Component ---
function ProtectedRoute({ children, roles }) {
  const { user, isLoading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <MDBox>Loading...</MDBox>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/authentication/sign-in" state={{ from: location }} replace />;
  }

  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/not-authorized" replace />;
  }

  return children;
}

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  roles: PropTypes.arrayOf(PropTypes.string),
};

// --- DashboardLayout Component ---
function DashboardLayout({ routes, onLogout, children }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, sidenavColor, openConfigurator } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { church } = useAuth();
  const { pathname } = useLocation();

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  return (
    <>
      <Sidenav
        color={sidenavColor}
        brandName={church?.name || "Ecclesia"}
        routes={routes}
        onMouseEnter={handleOnMouseEnter}
        onMouseLeave={handleOnMouseLeave}
        onLogout={onLogout}
      />
      <Configurator />
      {configsButton}
      <MDBox sx={{ position: "relative" }}>{children}</MDBox>
    </>
  );
}

DashboardLayout.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
  onLogout: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

// --- AppContent Component ---
function AppContent() {
  const { pathname } = useLocation();
  const { church, isLoading, user, logout, isAuthenticated } = useAuth();
  const [allAppRoutes, setAllAppRoutes] = useState(getRoutes());
  const navigate = useNavigate();

  // Load department routes when church data is available
  useEffect(() => {
    const loadAllRoutes = async () => {
      if (church?.id) {
        try {
          await Promise.all([loadDepartmentRoutes(church.id), loadMinistryRoutes(church.id)]);
          setAllAppRoutes(getRoutes());
        } catch (error) {
          console.error("Failed to load routes:", error);
        }
      }
    };
    loadAllRoutes();
  }, [church?.id]);

  const handleLogout = () => {
    logout();
  };

  const filterSidenavRoutesByRole = (routes) => {
    if (!user) return [];
    return routes
      .filter((route) => route.type !== "hidden")
      .flatMap((route) => {
        if (route.collapse) {
          const filteredCollapse = route.collapse.filter(
            (subRoute) => !subRoute.roles || subRoute.roles.includes(user?.role)
          );
          return filteredCollapse.length > 0 ? { ...route, collapse: filteredCollapse } : null;
        }
        if (!route.roles || route.roles.includes(user?.role)) {
          return route;
        }
        return null;
      })
      .filter(Boolean);
  };

  const sidenavRoutes = useMemo(() => getSidenavRoutes(), [allAppRoutes]);
  const filteredSidenavRoutes = useMemo(
    () => filterSidenavRoutesByRole(sidenavRoutes),
    [sidenavRoutes, user]
  );

  const getRoutesJSX = (routes) =>
    routes
      .flatMap((route) => {
        if (route.collapse) {
          return getRoutesJSX(route.collapse);
        }
        if (route.route) {
          if (route.protected) {
            return (
              <Route
                key={route.key}
                path={route.route}
                element={
                  <DashboardLayout routes={filteredSidenavRoutes} onLogout={handleLogout}>
                    <ProtectedRoute roles={route.roles}>{route.component}</ProtectedRoute>
                  </DashboardLayout>
                }
              />
            );
          } else {
            return <Route key={route.key} path={route.route} element={route.component} />;
          }
        }
        return null;
      })
      .filter(Boolean);

  const allRoutesJSX = useMemo(
    () => getRoutesJSX(allAppRoutes),
    [allAppRoutes, filteredSidenavRoutes, handleLogout]
  );

  if (isLoading) {
    return <MDBox>Loading...</MDBox>;
  }

  return (
    <Routes>
      {allRoutesJSX}
      <Route
        path="*"
        element={
          <Navigate to={isAuthenticated ? "/dashboard" : "/authentication/sign-in"} replace />
        }
      />
      <Route path="/not-authorized" element={<div>Unauthorized Access</div>} />
    </Routes>
  );
}

// --- App Component ---
export default function App() {
  const [rtlCache, setRtlCache] = useState(null);
  const [controller] = useMaterialUIController();
  const { direction, darkMode } = controller;

  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });
    setRtlCache(cacheRtl);
  }, []);

  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  return (
    <AuthProvider>
      {direction === "rtl" ? (
        <CacheProvider value={rtlCache}>
          <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
            <CssBaseline />
            <AppContent />
          </ThemeProvider>
        </CacheProvider>
      ) : (
        <ThemeProvider theme={darkMode ? themeDark : theme}>
          <CssBaseline />
          <AppContent />
        </ThemeProvider>
      )}
    </AuthProvider>
  );
}
